import React, { useContext } from 'react';
import { AppContext } from '@thd-nucleus/app-render';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { useLocation } from '@thd-olt-component-react/router';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { string, number } from 'prop-types';
import { useStore, useConfigService } from '@thd-nucleus/experience-context';
import {
  getContentfulPreviewCustomerType,
  getContentfulPreviewPathName
} from './browse-and-search-utils';

import { BrowseAndSearch } from './BrowseAndSearch';

export const BrowseAndSearchContainer = (props) => {
  const isBuyitagainEnabled = useConfigService('fs-prop:isBuyitagainEnabled');
  const { pageType, searchTimeout } = props;
  const isSearch = pageType === 'search';
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR && !isSearch;
  const store = useStore({ varnish, online: false });
  const { membershipInformation } = store;
  const { isCustomerIdentified = false } = useThdCustomer() || {};

  const browseDefaultVariables = {
    loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership,
    skipInstallServices: false,
    skipSpecificationGroup: false,
    isBrandPricingPolicyCompliant: false
  };
  if (browseDefaultVariables) {
    browseDefaultVariables.skipBuyitagain = !isBuyitagainEnabled;
    browseDefaultVariables.isBrandPricingPolicyCompliant = isCustomerIdentified;
  }

  const { pathname } = useLocation();
  const { instance } = useContext(AppContext);
  let customerType = instance?.customer?.type;
  const searchSkipFn = ({ skip, queryName, attributes }) => {
    if (queryName === 'searchStreamProduct') {
      if (!attributes?.searchModel?.products?._children?.fulfillment) return true;
    }
    return skip;
  };
  const isDiscoveryZonesEnabled = useConfigService('fs-prop:isDiscoveryZonesEnabled');
  const isDiscoveryZonesSearchEnabled = useConfigService('fs-prop:isDiscoveryZonesSearchEnabled');
  browseDefaultVariables.skipDiscoveryZones = !(isDiscoveryZonesEnabled && pageType === 'browse')
    && !(isDiscoveryZonesSearchEnabled && pageType === 'search');

  const searchOptsFn = ({ options, queryName }) => {
    if (queryName === 'searchStreamProduct') {
      const variables = options.variables;
      // eslint-disable-next-line no-param-reassign
      options.variables = undefined;
    }

    if (queryName === 'searchModel') {
      let to = 3000;
      if (searchTimeout) {
        if (typeof searchTimeout === 'string') {
          to = parseInt(searchTimeout, 10);
          // eslint-disable-next-line no-restricted-globals
          if (isNaN(to)) {
            to = 3000;
          }
        } else if (typeof searchTimeout === 'number') {
          to = searchTimeout;
        }
      }

      // eslint-disable-next-line no-param-reassign
      options.context = {
        ...(options.context || {}),
        timeout: to
      };
    }

    return options;
  };

  // --this is specific for being able to render pages for Contentful's previews
  const previewLink = instance?.headers?.['x-content-preview'];
  let finalPathname = pathname;
  if (previewLink) {
    finalPathname = getContentfulPreviewPathName(pathname);
    customerType = getContentfulPreviewCustomerType(pathname);
  }

  if (pageType === 'browse') {
    return (
      <QueryProvider
        skip={searchSkipFn}
        queryOptions={searchOptsFn}
        defaultVariables={browseDefaultVariables}
        cacheKey="browse-search-browse"
      >
        <BrowseAndSearch
          pathname={finalPathname}
          customerType={customerType}
          instance={instance}
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          {...props}
        />
      </QueryProvider>
    );
  }
  return (
    <QueryProvider
      defaultVariables={browseDefaultVariables}
      cacheKey="browse-search"
      forceClientQueriesOnServer={['searchModel', 'product']}
    >
      <BrowseAndSearch
        pathname={finalPathname}
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...props}
      />
    </QueryProvider>
  );

};
BrowseAndSearchContainer.propTypes = {
  pageType: string.isRequired,
  searchTimeout: string,
  productPodHoverDelay: number
};
BrowseAndSearchContainer.defaultProps = {
  searchTimeout: null,
  productPodHoverDelay: 500
};